import type { User } from "firebase/auth";

const getBackendBaseUrl = (): string => {
  const raw = process.env.REACT_APP_BACKEND_URL;
  if (!raw) {
    throw new Error("Missing REACT_APP_BACKEND_URL environment variable.");
  }
  return raw.replace(/\/+$/, "");
};

async function parseError(response: Response): Promise<string> {
  const data = (await response.json().catch(() => null)) as unknown;
  if (
    data &&
    typeof data === "object" &&
    "detail" in data &&
    typeof (data as { detail?: unknown }).detail === "string"
  ) {
    return (data as { detail: string }).detail;
  }
  return `Backend error (${response.status}).`;
}

export interface EntityActivityStat {
  count: number;
  firstCreatedAt: string | null;
  lastCreatedAt: string | null;
  note?: string;
}

export interface UsageBucket {
  current: number;
  limit?: number | null;
}

export interface OrganizationActivityLog {
  id?: string;
  eventType?: string | null;
  entityType?: string | null;
  entityId?: string | null;
  userId?: string | null;
  description?: string | null;
  createdAt?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface LogEntitySummary {
  total: number;
  created: number;
  updated: number;
  deleted: number;
  other: number;
  firstAt: string | null;
  lastAt: string | null;
}

export interface OrganizationActivityReport {
  organizationId: string;
  organizationName: string;
  email?: string | null;
  phone?: string | null;
  active: boolean;
  subscriptionStatus?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  generatedAt: string;
  entities: Record<string, EntityActivityStat>;
  usage: {
    planName?: string | null;
    limits?: Record<string, number> | null;
    salesPeople: UsageBucket;
    customers: UsageBucket;
    inventoryItems: UsageBucket;
    suppliers: UsageBucket;
    admins: UsageBucket;
  };
  logSummary: {
    byEntityType: Record<string, LogEntitySummary>;
    byEventType: Record<string, number>;
  };
  recentLogs: OrganizationActivityLog[];
  logsHasMore: boolean;
  agentBaseline: {
    purpose: string;
    signals: Record<string, boolean>;
  };
}

export const fetchOrganizationActivity = async (
  user: User,
  organizationId: string,
  logLimit = 50,
): Promise<OrganizationActivityReport> => {
  const token = await user.getIdToken();
  const baseUrl = getBackendBaseUrl();
  const search = new URLSearchParams({ logLimit: String(logLimit) });
  const response = await fetch(
    `${baseUrl}/api/v1/platform/organizations/${encodeURIComponent(
      organizationId,
    )}/activity?${search.toString()}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!response.ok) {
    throw new Error(await parseError(response));
  }
  return (await response.json()) as OrganizationActivityReport;
};
